import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Activity, CheckCircle2, Clock, Cpu, Database, XCircle } from 'lucide-react';
import { systemApi } from '@/api/system';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate, formatUptime } from '@/lib/utils';
import { useWorkspace } from '@/workspace/WorkspaceContext';


export function Overview(): JSX.Element {
  const { t } = useTranslation();
  const { currentBotId, currentBot } = useWorkspace();
  const { data, isLoading, error } = useQuery({
    queryKey: ['bot', 'system', currentBotId],
    queryFn: systemApi.get,
    enabled: !!currentBotId,
    refetchInterval: 30_000,
  });

  if (!currentBotId) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('overview.empty')}
      </div>
    );
  }
  if (isLoading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (error || !data) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">{t('common.backendUnreachable')}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t('overview.backendError')}
        </CardContent>
      </Card>
    );
  }

  const integrations = data.integrations;
  const enabledCount = integrations.filter((s) => s.configured && s.enabled).length;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {currentBot?.name ?? t('overview.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('overview.subtitle')}</p>
      </header>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Activity className="h-5 w-5" />}
          tint="emerald"
          label={t('overview.stat.backend')}
          value={t('overview.stat.online')}
          hint={`v${data.version} · ${data.nodeEnv}`}
        />
        <StatCard
          icon={<Clock className="h-5 w-5" />}
          tint="blue"
          label={t('overview.stat.uptime')}
          value={formatUptime(data.uptimeSeconds)}
        />
        <StatCard
          icon={<Cpu className="h-5 w-5" />}
          tint="indigo"
          label={t('overview.stat.llm')}
          value={data.llm.model}
          hint={data.llm.provider}
        />
        <StatCard
          icon={<Database className="h-5 w-5" />}
          tint="violet"
          label={t('overview.stat.rag')}
          value={data.rag?.provider ?? t('overview.stat.ragNotConfigured')}
          hint={
            data.rag?.knowledgeBaseId
              ? `KB: ${data.rag.knowledgeBaseId}`
              : data.rag
                ? t(`integrations.form.rag.statuses.${data.rag.status}`)
                : t('overview.stat.ragHint')
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('overview.integrations')}</span>
            <span className="text-sm font-normal text-muted-foreground">
              {t('overview.integrationsActive', {
                count: enabledCount,
                total: integrations.length,
              })}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {integrations.map((s) => {
            const effective = s.configured && s.enabled;
            return (
              <div
                key={s.instanceId}
                className="flex items-center justify-between rounded-md border p-3 text-sm"
              >
                <div className="flex items-center gap-2">
                  {effective ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="font-medium">
                    {s.label}
                  </span>
                </div>
                {effective ? (
                  <Badge variant="success">{t('overview.badge.on')}</Badge>
                ) : !s.configured ? (
                  <Badge variant="outline">{t('overview.badge.notConfigured')}</Badge>
                ) : (
                  <Badge variant="warning">{t('overview.badge.disabled')}</Badge>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('overview.retention')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row k={t('overview.retentionAudit')} v={t('overview.retentionDays', { count: data.retention.auditDays })} />
            <Row k={t('overview.retentionFeedback')} v={t('overview.retentionDays', { count: data.retention.feedbackDays })} />
            <Row k={t('overview.retentionChatOps')} v={t('overview.retentionDays', { count: data.retention.chatOpsDays })} />
            <Row k={t('overview.retentionChatSupport')} v={t('overview.retentionDays', { count: data.retention.chatSupportDays })} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('overview.bot')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row k={t('overview.botName')} v={data.bot?.name ?? '—'} />
            <Row k={t('overview.botId')} v={data.bot?.botId ?? '—'} />
            <Row k={t('overview.botStatus')} v={data.bot?.enabled ? t('common.active') : t('common.disabled')} />
            <Row k={t('overview.corsOrigins')} v={data.dashboardOrigins.join(', ')} />
            {currentBot ? <Row k={t('overview.updatedAt')} v={formatDate(currentBot.updatedAt)} /> : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const TINTS = {
  indigo: 'bg-primary/10 text-primary',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
} as const;

function StatCard({
  icon,
  label,
  value,
  hint,
  tint = 'indigo',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tint?: keyof typeof TINTS;
}): JSX.Element {
  return (
    <Card className="hover:shadow-md">
      <CardContent className="flex items-start gap-3.5 p-5">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${TINTS[tint]}`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="mt-1 truncate text-xl font-semibold tracking-tight">{value}</div>
          {hint ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{hint}</div> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b py-1 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="truncate font-mono text-xs">{v}</span>
    </div>
  );
}
